import React, { useState, useMemo } from "react";
import yaml from 'js-yaml';
import {
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ListBulletIcon,
} from "@heroicons/react/24/outline";

// Types
interface OutlineBlock {
  id: string;
  title: string;
  goal?: string;
  context?: string;
  description?: string;
}

interface OutlineData {
  sections?: OutlineBlock[];
}

interface DraftOutlinePreviewProps {
  contentText: string;
}

interface OutlineBlockItemProps {
  block: OutlineBlock;
  isExpanded: boolean;
  onToggle: () => void;
  sectionNumber?: number; // Auto-computed number for section_* blocks
}

/**
 * DraftOutlinePreview Component
 * Renders draft_outline.yaml as hierarchical outline blocks
 * Displays block title, context, description, and goal
 */
export default function DraftOutlinePreview_Component({ contentText }: DraftOutlinePreviewProps): JSX.Element {
  const [expandedBlocks, setExpandedBlocks] = useState<Set<string>>(new Set());

  // Parse YAML and handle errors
  const { parsedData, parseError } = useMemo(() => {
    try {
      const data = yaml.load(contentText) as OutlineData;
      return { parsedData: data, parseError: null };
    } catch (error) {
      return { parsedData: null, parseError: (error as Error).message };
    }
  }, [contentText]);

  const toggleBlock = (blockId: string): void => {
    const newExpanded = new Set(expandedBlocks);
    if (newExpanded.has(blockId)) {
      newExpanded.delete(blockId);
    } else {
      newExpanded.add(blockId);
    }
    setExpandedBlocks(newExpanded);
  };

  const toggleAll = (expand: boolean): void => {
    if (expand && parsedData?.sections) {
      setExpandedBlocks(new Set(parsedData.sections.map(block => block.id)));
    } else {
      setExpandedBlocks(new Set());
    }
  };

  // Error state - YAML parsing failed
  if (parseError) {
    return (
      <div className="h-full overflow-auto p-6 bg-cvc-canvas">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-red-900 mb-2">
                  Invalid YAML Format
                </h3>
                <p className="text-sm text-red-700 mb-3">
                  The outline YAML file could not be parsed. Please check the file format.
                </p>
                <pre className="text-xs text-red-600 bg-red-100 p-3 rounded font-mono overflow-x-auto">
                  {parseError}
                </pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No parsed data or no sections
  if (!parsedData?.sections || !Array.isArray(parsedData.sections)) {
    return (
      <div className="h-full overflow-auto p-6 bg-cvc-canvas">
        <div className="max-w-4xl mx-auto">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <div className="flex items-start space-x-2">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-yellow-900 mb-1">
                  No Outline Sections Available
                </h4>
                <p className="text-xs text-yellow-700">
                  The outline file appears to be empty or has no sections array.
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <pre className="text-xs text-gray-700 font-mono leading-relaxed whitespace-pre-wrap">
              {contentText}
            </pre>
          </div>
        </div>
      </div>
    );
  }

  const sectionCount = parsedData.sections.length;

  // Compute section numbers for main body sections (those with section_* prefix)
  const sectionNumbers = useMemo(() => {
    const numbers: Record<string, number> = {};
    let num = 1;
    for (const block of parsedData.sections) {
      if (block.id.startsWith('section_')) {
        numbers[block.id] = num++;
      }
    }
    return numbers;
  }, [parsedData.sections]);

  // Render all outline sections
  return (
    <div className="h-full overflow-auto bg-cvc-canvas">
      <div className="max-w-5xl mx-auto py-6 px-4">
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <ListBulletIcon className="h-5 w-5 text-purple-600" />
              <h2 className="text-base font-semibold text-gray-900">Draft Outline</h2>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => toggleAll(true)}
                className="text-xs text-purple-600 hover:text-purple-700 font-medium"
              >
                Expand All
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={() => toggleAll(false)}
                className="text-xs text-purple-600 hover:text-purple-700 font-medium"
              >
                Collapse All
              </button>
            </div>
          </div>

          <div className="divide-y divide-gray-100">
            {parsedData.sections.map((block, index) => (
              <OutlineBlockItem
                key={block.id || index}
                block={block}
                isExpanded={expandedBlocks.has(block.id)}
                onToggle={() => toggleBlock(block.id)}
                sectionNumber={sectionNumbers[block.id]}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Outline Block Item Component
 * Renders a single outline block with collapsible content
 */
function OutlineBlockItem({ block, isExpanded, onToggle, sectionNumber }: OutlineBlockItemProps): JSX.Element {
  // Display title with number prefix for main body sections
  const displayTitle = sectionNumber ? `${sectionNumber}. ${block.title}` : block.title;

  return (
    <div className="hover:bg-gray-50 transition-colors">
      <div
        onClick={onToggle}
        className="w-full flex items-start justify-between p-4 cursor-pointer"
      >
        <div className="flex items-start space-x-3 flex-1 min-w-0">
          {isExpanded ? (
            <ChevronDownIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
          ) : (
            <ChevronRightIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline space-x-2 mb-1">
              <span className="font-medium text-gray-900 text-sm">{displayTitle}</span>
            </div>
            {!isExpanded && block.goal && (
              <div className="text-xs text-gray-600 truncate mt-1">
                {block.goal}
              </div>
            )}
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 pl-12">
          <div className="space-y-4">
            {/* Goal */}
            {block.goal && (
              <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
                <div className="text-xs font-semibold text-purple-900 uppercase tracking-wide mb-1.5">
                  Goal
                </div>
                <div className="text-sm text-purple-900 leading-relaxed">
                  {block.goal}
                </div>
              </div>
            )}

            {/* Context */}
            {block.context && (
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                <div className="text-xs font-semibold text-blue-900 uppercase tracking-wide mb-1.5">
                  Context
                </div>
                <div className="text-sm text-blue-900 leading-relaxed">
                  {block.context}
                </div>
              </div>
            )}

            {/* Description */}
            {block.description && (
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1.5">
                  Description
                </div>
                <div className="text-sm text-gray-900 whitespace-pre-wrap leading-relaxed">
                  {block.description}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
